interface ValidationRule {
  name: string,
  regex: RegExp,
  regexFailMessage: string | null,
  minLength: number,
  maxLength: number,
  strict: boolean,
  updateWhen?: (val: string) => boolean,
}

type ValidationRules = { [context: string]: ValidationRule }

// rules to validate each input.

export const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const validationRules: ValidationRules = {
  namePrefix: {
    name: 'Prefix',
    regex: /^[a-zA-Z., ]{0,10}$/, // regex to test
    regexFailMessage: null, // if we want to display error message on this input?
    minLength: 0,
    maxLength: 10,
    strict: true, // validate this input as the user typing
    updateWhen: (val: string) => /^[a-zA-Z., ]{0,10}$/.test(val), // only allow letters, '.', ','
  },
  nameFirst: {
    name: 'First Name',
    regex: /^[a-zA-Z'\- ]{1,50}$/,
    regexFailMessage: null,
    minLength: 1,
    maxLength: 50,
    strict: true,
    updateWhen: (val: string) => /^[a-zA-Z'\- ]{0,50}$/.test(val),
  },
  nameMiddle: {
    name: 'Middle Initial',
    regex: /^[a-zA-Z']{1}$/,
    regexFailMessage: null,
    minLength: 1,
    maxLength: 1,
    strict: true,
    updateWhen: (val: string) => /^[a-zA-Z']{0,1}$/.test(val),
  },
  nameLast: {
    name: 'Last Name',
    regex: /^[a-zA-Z'\- ]{1,50}$/,
    regexFailMessage: null,
    minLength: 1,
    maxLength: 50,
    strict: true,
    updateWhen: (val: string) => /^[a-zA-Z'\- ]{0,50}$/.test(val),
  },
  nameSuffix: {
    name: 'Suffix',
    regex: /^[a-zA-Z., ]{0,10}$/,
    regexFailMessage: null,
    minLength: 0,
    maxLength: 10,
    strict: true,
    updateWhen: (val: string) => /^[a-zA-Z., ]{0,10}$/.test(val),
  },
  contactEmail: {
    name: 'Email Address',
    regex: emailRegex,
    regexFailMessage: 'Please enter a valid Email Address',
    minLength: 0,
    maxLength: 50,
    strict: false, // here we dont care what a user is typing will check if valid onsubmit
  },
  contactPhone: {
    name: 'Phone Number',
    regex: /^[0-9]{10}$/,
    regexFailMessage: 'Please enter a 10-digit Phone Number',
    minLength: 10,
    maxLength: 10,
    strict: true,
    updateWhen: (val: string) => /^[0-9]{0,10}$/.test(val),
  },
  addressLine1: {
    name: 'Address',
    regex: /^.{5,50}$/,
    regexFailMessage: 'Please enter a valid Address',
    minLength: 3,
    maxLength: 50,
    strict: true,
    updateWhen: (val: string) => /^.{0,50}$/.test(val),
  },
  addressLine2: {
    name: 'Address',
    regex: /^.{0,20}$/,
    regexFailMessage: 'Please enter a valid Address',
    minLength: 0,
    maxLength: 20,
    strict: true,
    updateWhen: (val: string) => /^.{0,20}$/.test(val),
  },
  addressCity: {
    name: 'City',
    regex: /^[a-zA-Z'\- ]{1,50}$/,
    regexFailMessage: 'Please enter a valid City',
    minLength: 1,
    maxLength: 50,
    strict: true,
    updateWhen: (val: string) => /^[a-zA-Z'\- ]{0,50}$/.test(val),
  },
  addressState: {
    name: 'State',
    regex: /^[A-Z]{2}$/,
    regexFailMessage: 'Please select a State',
    minLength: 2,
    maxLength: 2,
    strict: true,
    updateWhen: (val: string) => /^[A-Z]{0,2}$/.test(val),
  },
  addressZIP: {
    name: 'Zip',
    regex: /^\d{5}([-]?\d{4})?$/,
    regexFailMessage: 'Please enter a valid ZIP',
    minLength: 5,
    maxLength: 10,
    strict: true,
    updateWhen: (val: string) => {
      const canUpdate = /^\d{0,5}$/.test(val)
      || /^\d{5}[-]$/.test(val)
      || /^\d{5}[-]\d{1,3}$/.test(val)
      return canUpdate
    },
  }
}

const validateInput = (inputName: string, inputVal: string, lastVal: string) => {
  let response: { error: string | null, updatedVal: string | null, warn: boolean, valid: boolean } = {
    error: null,
    updatedVal: inputVal,
    warn: false,
    valid: true
  }
  const setError = (message: string, val: string = inputVal, warning: boolean = false) => {
    response.error = message
    response.updatedVal = val
    response.warn = warning ? true : false
    response.valid = warning ? true : false
  }
  // does input have a rules object?
  const rules = validationRules[inputName]
  if (!rules) {
    return response
  }
  const passedRegex = rules.regex.test(inputVal);
  // check if we're validating this one as they type
  // check if we're about to update the input value
  let canUpdate = true
  if (!passedRegex && rules.strict && rules.updateWhen) {
    canUpdate = rules.updateWhen(inputVal)
  }
  // show generic error message for input
  if (canUpdate && !passedRegex && rules.regexFailMessage) {
    setError(
      rules.regexFailMessage,
      canUpdate ? inputVal : lastVal,
      false
    )
    return response;
  }
  if (!canUpdate) {
    // don't update input
    response.updatedVal = lastVal
  }
  return response
}

export default validateInput;
